import React from "react";
import ClaimButton from "@components/FlowActionButtons/ClaimButton";
import { useNotification } from "@context/NotificationContext";
import { claimGatepassById } from "@services/gatepassDataApi";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { useTranslation } from "react-i18next";
import { canUserClaim } from "./utils";

export interface GatepassFlowData {
  id: string;
  // Gatepass number displayed to users (e.g. 882802433)
  serialNumber?: string | number;
  status: string;
  // Sponsor company of the gatepass
  sponsorCompany?: string | null;
  // True when requester company is same as sponsor company and sponsor step is skipped.
  skipSponsorCompanyApproval?: boolean;
  task: {
    taskId: string;
    role: string;
    workflowId: string;
    runId: string;
    status: string;
    claimOwnerName: string | null;
  } | null;
}

interface ClaimGatepassProps {
  gatepassFlowData?: GatepassFlowData;
}

const LANG_BASE = "gatepass";

const ClaimGatepass: React.FC<ClaimGatepassProps> = ({ gatepassFlowData }) => {
  const service = "bff-gatepass";
  const { notify } = useNotification();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const claimGatepassMutation = useMutation({
    mutationFn: (id: string) =>
      runTransaction(
        {
          module: "gatepass",
          screen: "GatepassDetails",
          action: "claim_gatepass",
          entityType: "gatepass",
        },
        (transactionId) =>
          claimGatepassById({
            service,
            id,
            ...(transactionId ? { transactionId } : {}),
          }),
      ),
    onSuccess: async () => {
      await queryClient.refetchQueries({
        queryKey: ["gatepass-request-preview", gatepassFlowData?.id],
      });
      notify({
        title: t("gatepass.claim-success", { defaultValue: "Gatepass claimed" }),
        type: "success",
      });
      await queryClient.invalidateQueries({ queryKey: ["gatepasses"] });
    },
    onError: (error: Error) =>
      notify({
        title: t("common.error"),
        description: error?.message ?? t("common.something-went-wrong"),
        type: "error",
      }),
  });

  if (!gatepassFlowData?.task) {
    return null;
  }

  const isActionAvailable = Boolean(
    canUserClaim(
      gatepassFlowData.task.status,
      gatepassFlowData.status,
      gatepassFlowData.task.role,
      gatepassFlowData.task.claimOwnerName,
    ),
  );

  const handleClaimClick = () => {
    claimGatepassMutation.mutate(gatepassFlowData.id);
  };

  return (
    <ClaimButton
      langBase={LANG_BASE}
      permission="canClaimGatepass"
      isActionAvailable={isActionAvailable}
      onClaim={handleClaimClick}
    />
  );
};

export default ClaimGatepass;
